import { Host, OlaggModule, PermissionEnum } from "@olagg/db-types";
import { useEffect, useState } from "react";
import { Table as ChakraTable, Flex, TableContainer, Thead, Tr, Th, Tbody, Td, Button, Image } from "@chakra-ui/react";
import moment from "moment";
import Pagination from "@olagg/ui-kit/Pagination";
import { useNavigate, useSearchParams } from "react-router-dom";
import useRoles from "../../features/Roles/hooks/useRoles";
import useHosts from "./hooks/useHosts";

const Actions = ({ host }: { host: Host }) => {
    const { missingPermission } = useRoles();
    const { deleteHost } = useHosts();
    const navigate = useNavigate();
    const viewHost = (event: Host) => navigate(`/hosts/${event.id}`);
    const removeHost = (event: Host) => {
        if (window.confirm('¿Estás seguro que deseas eliminar este host?')) {
            deleteHost(event.id).then(() => {
                window.location.reload();
            })
        }
    }

    return (
        <Flex gap='10px'>
            <Button isDisabled={missingPermission(OlaggModule.Event, PermissionEnum.EDIT)} bgColor={'#37A169'} p='0 10px' h='30px' fontSize={'14px'} onClick={() => viewHost(host)}>
                Ver
            </Button>
            <Button isDisabled={missingPermission(OlaggModule.Event, PermissionEnum.EDIT)} bgColor={'#E53E3E'} p='0 10px' h='30px' fontSize={'14px'} onClick={() => removeHost(host)}>
                Eliminar
            </Button>
        </Flex>
    )
}

const Hosts = () => {
    const [hosts, setHosts] = useState<Host[]>([]);
    const [count, setCount] = useState(0);
    const [searchParams, setSearchParams] = useSearchParams();
    const [filter, setFilter] = useState({
        title: searchParams.get('title') || '',
        page: parseInt(searchParams.get('page') || '1'),
        limit: parseInt(searchParams.get('limit') || '10')
    });

    const { getHosts } = useHosts();
    const onFilter = ({
        name,
        value
    }: EventTarget & HTMLInputElement) => {
        const parsedValue = (name === 'page' || name === 'limit') ? parseInt(value) : value;
        
        setFilter({ ...filter, [name]: parsedValue });

        if (value) searchParams.set(name, value);
        else searchParams.delete(name);
        setSearchParams(searchParams);
    }

    const { page, limit, name } = filter

    const fetchHosts = async () => {
        try {
            const response = await getHosts({ 
                limit, 
                page, 
            });
            setHosts(response.hosts);
            setCount(response.count);
        } catch (error) {
            console.error('Error fetching hosts:', error);
        }
    };

    useEffect(() => {
        fetchHosts();
    }, [page, limit]);

    useEffect(() => {
        const urlPage = searchParams.get('page');
        const urlLimit = searchParams.get('limit');
        const urlTitle = searchParams.get('title');

        if (urlPage || urlLimit || urlTitle) {
            setFilter({
                title: urlTitle || '',
                page: parseInt(urlPage || '1'),
                limit: parseInt(urlLimit || '10')
            });
        }
    }, []);

    if (hosts.length === 0) {
        return (
            <Flex w={'full'} justifyContent='center' flexDirection={'column'} alignItems='center' pt='20px'>
                <h2>No hay hosts disponibles</h2>
            </Flex>
        );
    }

    return (
        <Flex w={'full'} pb={4} justifyContent='center' flexDirection={'column'}>
            <Flex w='full' justifyContent='center' px={'20px'} pb={'15px'}>
                <TableContainer overflowX={'scroll'} overflowY={'scroll'} w="100%" maxH="60vh">
                    <ChakraTable
                        variant="striped"
                        size="sm"
                        colorScheme="olaggPurple"
                        fontSize="18px"
                        position='relative'
                        whiteSpace={'normal'}
                    >
                        <Thead>
                            <Tr position='sticky' bg='gray.800' top='0' zIndex='100'>
                                <Th py={'20px'} color="white" colSpan={2}>Host</Th>
                                <Th py={'20px'} color="white">Link</Th>
                                <Th py={'20px'} color="white">Creado</Th>
                                <Th py={'20px'} color="white">Actualizado</Th>
                                <Th py={'20px'} color="white">Acciones</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {hosts.map((item, index) => (
                                <Tr key={index} color="white">
                                    <Td py={'20px'}><Image src={item.avatar} w="20px" h="20px" /></Td>
                                    <Td py={'20px'}>{item.name}</Td>
                                    <Td py={'20px'}>{item.profileLink}</Td>
                                    <Td py={'20px'}>{item.createdAt && moment(item.createdAt).format('DD/MM/YYYY')}</Td>
                                    <Td py={'20px'}>{item.updatedAt && moment(item.updatedAt).format('DD/MM/YYYY')}</Td>
                                    <Td py={'20px'}>
                                        <Actions host={item} />
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </ChakraTable>
                </TableContainer>
            </Flex>

            <Flex px={'20px'} w='full' justifyContent={'center'}>

                <Pagination
                    params={{ limit, page, count }}
                    onClick={page =>
                        onFilter({
                            name: 'page',
                            value: String(page + 1)
                        } as EventTarget & HTMLInputElement)
                    }
                    onLimitChange={val =>
                        onFilter({
                            name: 'limit',
                            value: val
                        } as EventTarget & HTMLInputElement)
                    }
                    hideLimitInput
                />
            </Flex>
        </Flex>
    )
};

export default Hosts;